import { apiClient } from "../api/ApiClient";
import { IAdAdapter } from "./adapters/IAdAdapter";
import { AdsgramAdapter } from "./adapters/AdsgramAdapter";
import { RichAdsAdapter } from "./adapters/RichAdsAdapter";
import { AdexiumAdapter } from "./adapters/AdexiumAdapter";
import { MonetagAdapter } from "./adapters/MonetagAdapter";

/** Max time to wait for requestAd (fill check) per adapter */
const REQUEST_TIMEOUT = 2000;
/** Max time to wait for ad display + user watching */
const DISPLAY_TIMEOUT_REWARDED = 45000;
const DISPLAY_TIMEOUT_INTER = 30000;

interface AdWaterfallEntry {
  name: string;
  enabled: boolean;
  rewardedBlockId?: string;
  interBlockId?: string;
}

class AdManager {
  private adapters: IAdAdapter[] = [];
  private lastInterstitialTime = 0;
  private initialized = false;

  init() {
    if (this.initialized) return;
    this.initialized = true;

    let waterfall = apiClient.configValue<AdWaterfallEntry[]>("ad_waterfall", []);
    if (typeof waterfall === "string") {
      try { waterfall = JSON.parse(waterfall); } catch { waterfall = []; }
    }

    for (const entry of waterfall) {
      if (!entry.enabled) continue;
      if (entry.name === "adsgram") {
        this.adapters.push(new AdsgramAdapter(entry.rewardedBlockId ?? "", entry.interBlockId ?? ""));
      } else if (entry.name === "richads" && entry.rewardedBlockId) {
        this.adapters.push(new RichAdsAdapter(entry.rewardedBlockId));
      } else if (entry.name === "adexium" && entry.rewardedBlockId) {
        this.adapters.push(new AdexiumAdapter(entry.rewardedBlockId));
      } else if (entry.name === "monetag" && entry.rewardedBlockId) {
        this.adapters.push(new MonetagAdapter(entry.rewardedBlockId));
      }
    }

    console.log("[AdManager] adapters:", this.adapters.map(a => a.name).join(", "));
  }

  private getUserLevel(): number {
    try {
      const saved = localStorage.getItem("sio_cookie_tg");
      if (saved) return JSON.parse(saved).absoluteLevelNum ?? 1;
    } catch {}
    return 1;
  }

  /**
   * Two-phase waterfall:
   * Phase 1 — requestAd (2s timeout per adapter): who has fill?
   * Phase 2 — showAd (45s): user watches the ad
   */
  async showRewarded(): Promise<boolean> {
    this.init();

    const level = this.getUserLevel();
    const minLevel = apiClient.configValue("ad_min_level_rewarded", 3);
    if (level < minLevel) return false;

    const available = this.adapters.filter(a => a.isAvailable());
    if (available.length === 0) return false;

    for (const adapter of available) {
      const t0 = Date.now();
      try {
        // Phase 1: fast fill check
        const hasFill = await withTimeout(adapter.requestAd("rewarded"), REQUEST_TIMEOUT);
        const requestMs = Date.now() - t0;
        console.log("[Ad] %s requestAd=%dms fill=%s", adapter.name, requestMs, hasFill);

        if (!hasFill) continue;

        // Phase 2: display + watch
        const result = await withTimeout(adapter.showAd(), DISPLAY_TIMEOUT_REWARDED);
        const totalMs = Date.now() - t0;
        console.log("[Ad] %s showAd=%dms result=%s", adapter.name, totalMs, result);
        apiClient.trackEvent("ad_view", { adapter: adapter.name, type: "rewarded", completed: result, ms: totalMs });
        return result;

      } catch (err) {
        const ms = Date.now() - t0;
        console.warn("[Ad] %s fail %dms: %s", adapter.name, ms, (err as Error)?.message);
        apiClient.trackEvent("ad_error", { adapter: adapter.name, type: "rewarded", ms, err: (err as Error)?.message?.slice(0, 100) });
        // Next adapter
      }
    }
    return false;
  }

  async showInterstitial(): Promise<void> {
    this.init();

    const level = this.getUserLevel();
    const minLevel = apiClient.configValue("ad_min_level_interstitial", 10);
    if (level < minLevel) return;

    const cooldownSec = apiClient.configValue("inter_ad_cooldown_sec", 180);
    if (Date.now() - this.lastInterstitialTime < cooldownSec * 1000) return;

    const available = this.adapters.filter(a => a.isAvailable());

    for (const adapter of available) {
      try {
        const hasFill = await withTimeout(adapter.requestAd("interstitial"), REQUEST_TIMEOUT);
        if (!hasFill) continue;

        await withTimeout(adapter.showAdInterstitial(), DISPLAY_TIMEOUT_INTER);
        this.lastInterstitialTime = Date.now();
        apiClient.trackEvent("ad_view", { adapter: adapter.name, type: "interstitial" });
        return;
      } catch {
        apiClient.trackEvent("ad_error", { adapter: adapter.name, type: "interstitial" });
      }
    }
  }

  hasAdapters(): boolean {
    this.init();
    return this.adapters.length > 0;
  }
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error("timeout " + ms + "ms")), ms)
    ),
  ]);
}

export const adManager = new AdManager();
